<template>
  <div class="table-classic-new">
    <div
      v-if="checkedRowsCounter && !children"
      class="selection-counter"
    >
      <p class="label-counter flex-vcenter text-regular">
        {{ $t("filtre.selection") }}
      </p>
      <Counter :value="checkedRowsCounter" />
      <button
        class="button flex-vcenter"
        @click="handleUncheckAll"
      >
        <UilTimes size="24" />
      </button>
    </div>

    <table>
      <thead>
        <tr>
          <th
            v-if="checkboxes"
            class="checkbox"
          >
            <div>
              <ButtonCheckbox
                v-if="!ficheTable"
                v-model="headerChecked"
                name="header-checkbox"
                :mix="headerMixed"
                :label="labelToutSelectionner"
                @update:modelValue="handleToggleAll()"
              />
              <ButtonCheckbox
                v-else
                v-model="headerCheckedFiche"
                name="header-checkbox"
                :mix="headerMixedFiche"
                :label="labelToutSelectionner"
                @update:modelValue="handleToggleAllFiche()"
              />
            </div>
          </th>
          <th
            v-for="col in columns"
            :key="col.key"
            :class="[col.key, ...(col.classes || []), ]"
          >
            <component
              :is="col.notSortable ? 'div' : 'button'"
              class="button-filter"
              @click="!col.notSortable && sort(col)"
            >
              <UisSorting
                v-if="!col.notSortable"
                size="16"
              />
              {{ col.name }}
              <component
                :is="col.icon"
                v-if="col.icon"
                size="16"
              />
            </component>
          </th>
        </tr>
        <tr class="separator" />
      </thead>
      <tbody>
        <template
          v-for="(row, index) in rows"
          :key="index"
        >
          <tr
            :class="[
              row._variant,
              `ligne-${row.id}`,
              row.product_link?.id && allowProductClick ? 'link' : 'unlinked',
              {
                highlighted: checkedRowsMap[row.id],
                disabled: !row._routeName,
                expanded: row._expanded,
              },
            ]"
            @click="handleRowClick(row)"
          >
            <td
              v-if="checkboxes"
              class="checkbox"
            >
              <div>
                <ButtonCheckbox
                  :model-value="checkedRowsMap[row.id]"
                  class="visibility-hidden"
                  name="header-checkbox"
                  :mix="mixedRowsMap[row.id]"
                  @update:modelValue="handleRowCheckChange(row, !checkedRowsMap[row.id] || mixedRowsMap[row.id])"
                  @click.native.stop
                />
              </div>
            </td>
            <td
              v-for="col in columns"
              :key="col.key"
              v-title-overflow
              :class="[col.key, ...(col.classes || []), ]"
              :data-label="col.name"
              @click="!row._disabled ? handleColClick(col, row) : null"
            >
              <!-- @slot Emplacement pour le contenu des cellules du tableau. -->
              <slot
                :name="col.key"
                :value="row"
              >
                <!-- On met une valeur par défaut au cas où on oublierait de surdéfinir le rendu -->
                {{ row[col.key] }}
              </slot>
            </td>
          </tr>
          <tr
            v-if="row._additional"
            :key="`additional_${index}`"
            class="additional-line"
          >
            <!-- Prend toute la largeur du tableau -->
            <td :colspan="checkboxes ? columns.length + 1 : columns.length">
              <!-- @slot Emplacement pour des éléments sous les lignes. -->
              <slot
                name="additional-line"
                :value="row"
              />
            </td>
          </tr>
          <tr
            v-if="row._children?.length && row._expanded"
            :key="`child_${index}`"
            class="child-row"
          >
            <td :colspan="checkboxes ? columns.length + 1 : columns.length">
              <!-- @slot Emplacement pour des éléments sous les lignes. -->
              <slot
                name="child-row"
                :value="row"
              />
            </td>
          </tr>
          <tr
            class="separator"
          />
        </template>
      </tbody>
    </table>
  </div>
</template>

<script>
import { mapGetters } from "vuex";

import { ButtonCheckbox, Counter } from "@lde/core_lde_vue";
import { UilTimes } from "@lde/vue-unicons";
import { UisSorting } from "@lde/vue-unicons-solid";

import { getNestedProperty } from "@/modules/utils";

/**
 * Affiche un "tableau" interactif (dropdown, bouton, input…) avec son contenu.
 * Il est possible de trier d'après les colonnes.
 * Le tableau peut contenir un tableau imbriqué, dont les slots devront commencer par "child-".
 */
export default {
  name: "TableClassic",
  components: {
    ButtonCheckbox,
    UisSorting,
    Counter,
    UilTimes,
  },
  model: {
    prop: "items",
    event: "change",
  },
  props: {
    children: {
      type: Boolean,
      default: false,
    },
    /**
     * En-têtes du tableau.
     */
    columns: {
      type: Array,
      default: () => [],
    },
    /**
     * Lignes du tableau.
     */
    rows: {
      type: Array,
      required: true,
    },
    /**
     * Nombre total de lignes.
     */
    totalRows: {
      type: Number,
      required: false,
      default: 0,
    },
    /**
     * Permet d'afficher des checkboxes en début de ligne.
     */
    checkboxes: {
      type: Boolean,
      default: false,
    },
    /**
     * Définit si le tri se fait via l'API ou en front.
     */
    internalSort: {
      type: Boolean,
      default: false,
    },
    /**
     * Clé et sens du tri des lignes du tableau pour l'API (non nécessaire si internalSort === true).
     */
    activeSort: {
      type: Object,
      default: undefined,
    },
    /**
     * Activer ou non l'overture du produit sur une ligne de table.
     */
    allowProductClick: {
      type: Boolean,
      default: false,
    },
  },
  emits: [
    /**
     * Déclenché au clic sur un des boutons de tri.
     * @param {Object} - Clé et sens du tri.
     */
    "sort",
  ],
  data() {
    return {
      internalActiveSort: {},
    };
  },
  computed: {
    ...mapGetters("TableClassic", [
      "checkedRowsIds",
      "nbCheckedRows",
      "allChecked",
      "excludedIds",
      "allIds",
    ]),
    ...mapGetters(["user"]),
    ficheTable() {
      return this.$route.name.includes("_item");
    },
    // Gère quel chiffre utiliser pour le compteur des checked rows.
    checkedRowsCounter() {
      return this.ficheTable ? this.rows.filter((row) => row._checked).length : this.nbCheckedRows;
    },
    // Compte par tableau : sur le panier, nbCheckedRows couvre tous les blocs catalogue à la fois
    labelToutSelectionner() {
      const label = this.$t("action.tout-selectionner");
      const nb = this.rows.filter((row) => (this.ficheTable ? row._checked : this.checkedRowsMap[row.id])).length;
      return nb ? `${label} (${nb})` : label;
    },
    /**
     * Tableau des IDs cochés (pour usage interne réactif).
     */
    checkedIdsArray() {
      if (this.allChecked) {
        // En mode "tout coché", retourner allIds filtré par les exclus
        return this.allIds.filter((id) => !this.excludedIds.includes(id));
      }
      return Array.isArray(this.checkedRowsIds) ? this.checkedRowsIds : [];
    },
    /**
     * Tableau des IDs cochés (pour usage interne réactif). Version Fiche.
     */
    checkedIdsArrayFiche() {
      return this.rows
        .filter((row) => row._checked)
        .map((row) => row.id);
    },
    /**
     * Map réactive des lignes cochées pour forcer la réactivité dans le template.
     * Retourne un objet { [id]: boolean } pour chaque ligne visible.
     */
    checkedRowsMap() {
      const map = {};
      this.rows.map((row) => {
        if (row._children?.length) {
          // Pour les lignes avec enfants, cochée si au moins un enfant est coché
          const childrenChecked = row._children.map((child) => ({
            id: child.id,
            checked: this.isIdCheckedComputed(child.id),
          }));
          map[row.id] = childrenChecked.some((c) => c.checked);
        } else {
          // Ligne simple
          map[row.id] = this.isIdCheckedComputed(row.id);
        }
        return row;
      });
      return map;
    },
    /**
     * Map réactive de l'état "mixed" pour les lignes avec enfants.
     */
    mixedRowsMap() {
      const map = {};
      this.rows.map((row) => {
        if (row._children?.length) {
          const checkedCount = row._children.filter((child) => this.isIdCheckedComputed(child.id)).length;
          // Mixed si certains enfants sont cochés mais pas tous
          map[row.id] = checkedCount > 0 && checkedCount < row._children.length;
        } else {
          map[row.id] = false;
        }
        return row;
      });
      return map;
    },
    /**
     * État de la checkbox du header.
     * Cochée si allChecked est true ou s'il y a au moins une ligne cochée (visible ou non).
     */
    headerChecked: {
      get() {
        if (this.allChecked) {
          return true;
        }
        // En mode manuel, cochée si au moins une ligne est cochée (sur n'importe quelle page)
        return this.nbCheckedRows > 0;
      },
      set() {
        // La valeur est gérée par handleToggleAll
      },
    },
    /**
     * État de la checkbox du header.
     * Comportement différent dans fiche liste / devis / commande. On le sépare des données dans le store.
     */
    headerCheckedFiche: {
      get() {
        return this.rows.some((row) => this.checkedIdsArrayFiche?.includes(row.id));
      },
      set() {
        // La valeur est gérée par handleToggleAllFiche
      },
    },
    /**
     * État "mixed" de la checkbox du header.
     * True si certaines lignes sont cochées mais pas toutes.
     */
    headerMixed() {
      if (this.allChecked) {
        // En mode "tout coché", mixed si des éléments sont exclus
        return this.excludedIds.length > 0;
      }
      // En mode manuel - utiliser checkedRowsMap pour la réactivité
      const visibleCheckedCount = this.rows.filter((row) => this.checkedRowsMap[row.id]).length;
      const totalCheckedCount = this.nbCheckedRows;
      // Mixed si :
      // - Il y a des lignes cochées (visibles ou non) mais pas toutes
      // - Autrement dit : au moins une cochée ET moins que le total
      if (totalCheckedCount > 0 && totalCheckedCount < this.totalRows) {
        return true;
      }
      // Cas particulier : toutes les lignes visibles sont cochées mais il y a d'autres pages
      if (visibleCheckedCount === this.rows.length
        && this.rows.length < this.totalRows
        && visibleCheckedCount > 0) {
        return true;
      }
      return false;
    },
    /**
     * État "mixed" de la checkbox du header.
     * Comportement différent dans fiche liste / devis / commande. On le sépare des données dans le store.
     */
    headerMixedFiche() {
      const checkedInRows = this.rows.filter((row) => row._checked);
      return checkedInRows.length && checkedInRows.length !== this.totalRows;
    },
  },
  watch: {
    rows: {
      handler(newRows) {
        this.updateRowsVisualState(newRows);
      },
      immediate: true,
      deep: true,
    },
    /**
     * Watcher sur les changements du store pour mettre à jour l'état visuel des lignes parentes.
     */
    nbCheckedRows() {
      this.updateRowsVisualState(this.rows);
    },
    excludedIds: {
      handler() {
        this.updateRowsVisualState(this.rows);
      },
      deep: true,
    },
    totalRows: {
      handler() {
        this.$store.commit("TableClassic/setTotalRows", this.totalRows);
      },
      immediate: true,
    },
  },
  methods: {
    uncheckAll() {
      this.rows.forEach((row) => {
        row._checked = false;
        row._mixed = false;

        if (row._children?.length) {
          row._children.forEach((child) => {
            child._checked = false;
          });
        }
      });
      this.$store.dispatch("TableClassic/clearCheckedRows");
    },
    /**
     * Vérifie si une ligne est cochée (méthode utilitaire).
     * @param {number|string} id - L'ID de la ligne.
     * @returns {boolean} True si la ligne est cochée.
     */
    isRowCheckedById(id) {
      return this.isIdCheckedComputed(id);
    },
    /**
     * Vérifie si un ID est coché (utilise les computed pour la réactivité).
     * @param {number|string} id - L'ID à vérifier.
     * @returns {boolean} True si l'ID est coché.
     */
    isIdCheckedComputed(id) {
      if (this.allChecked) {
        return !this.excludedIds.includes(id);
      }
      if (this.ficheTable) {
        return this.checkedIdsArrayFiche?.includes(id);
      }
      return this.checkedIdsArray?.includes(id) || false;
    },
    /**
     * Met à jour l'état visuel des lignes (checked, mixed) en fonction du store.
     * @param {Array} rows - Les lignes à mettre à jour.
     */
    updateRowsVisualState(rows) {
      rows.map((row) => {
        if (row._children?.length) {
          // En mode allChecked, mettre à jour visuellement les enfants
          if (this.allChecked) {
            row._children.map((child) => {
              const isExcluded = this.excludedIds.includes(child.id);
              child._checked = !isExcluded;
              return child;
            });
          }
          if (!this.ficheTable) {
            // page principale
            // Coche une ligne parente si au moins une sous-ligne est cochée
            const hasCheckedChild = row._children.some((child) => this.isRowCheckedById(child.id));
            row._checked = hasCheckedChild;

            // Variante "mix" pour une ligne si au moins une sous-ligne est cochée, mais pas toutes
            const allChildrenChecked = row._children.every((child) => this.isRowCheckedById(child.id));
            row._mixed = hasCheckedChild && !allChildrenChecked;
          } else {
            // page fiche
            const hasCheckedChild = row._children.some((child) => this.isRowCheckedById(child.id));
            row._checked = hasCheckedChild;

            const allChildrenChecked = row._children.every((child) => this.isRowCheckedById(child.id));
            row._mixed = hasCheckedChild && !allChildrenChecked;
          }
        }
        return row;
      });
    },
    /**
     * Gère le changement d'état d'une checkbox de ligne.
     * @param {Object} row - La ligne concernée.
     * @param {boolean} checked - Nouvel état de la checkbox.
     */
    handleRowCheckChange(row, checked) {
      if (row._children?.length) {
        // Si la ligne a des enfants, utiliser toggleParent avec le counter
        row._checked = checked;
        row._mixed = false;
        row._children.map((child) => {
          child._checked = checked;
          return child;
        });
        // Utiliser row.counter pour le nombre total d'enfants (pas _children.length qui est paginé)
        this.$store.dispatch("TableClassic/toggleParent", {
          row,
          checked,
          counter: row.counter || row._children.length,
        });
      } else {
        // Ligne simple
        this.$store.dispatch("TableClassic/toggleRow", { row, checked });
        // gestion checkbox fiche
        row._checked = !row._checked;
      }
      // Forcer la mise à jour de la vue après modification du store
      this.$nextTick(() => {
        this.updateRowsVisualState(this.rows);
      });
    },
    /**
     * Décoche toutes les lignes du tableau.
     */
    handleUncheckAll() {
      this.$store.dispatch("TableClassic/clearCheckedRows");
      // Mettre à jour visuellement les lignes
      this.rows.map((row) => {
        row._checked = false;
        row._mixed = false;
        if (row._children?.length) {
          row._children.map((child) => {
            child._checked = false;
            return child;
          });
        }
        return row;
      });
    },
    /**
     * Coche/décoche toutes les lignes du tableau.
     */
    handleToggleAll() {
      this.$store.dispatch("TableClassic/toggleAll");
      // Mettre à jour visuellement les lignes après le toggleAll
      this.$nextTick(() => {
        const shouldBeChecked = this.allChecked;
        this.rows.map((row) => {
          const isExcluded = this.excludedIds.includes(row.id);
          row._checked = shouldBeChecked && !isExcluded;
          row._mixed = false;
          if (row._children?.length) {
            row._children.map((child) => {
              const childIsExcluded = this.excludedIds.includes(child.id);
              child._checked = shouldBeChecked && !childIsExcluded;
              return child;
            });
          }
          return row;
        });
      });
    },
    handleToggleAllFiche() {
      const allChecked = this.rows.every((row) => row._checked);

      this.rows.forEach((row) => {
        row._checked = !allChecked;
      });
    },
    /**
     * Effectue une action au clic sur une colonne.
     * Si la colonne a un attribut "link", il est prioritaire sur le clic de la ligne.
     * Pour les lignes:
     * Dans le cas d'un lien, on gère le fait que la ligne puisse être un produit et qu'il faut se baser sur son id
     * selon son type d'offre.
     * @param {Object} col Infos de la cellule.
     * @param {Number} ligne Infos de la ligne.
     */
    handleColClick(col, ligne) {
      if (col.link) {
        const params = { name: col.link };
        if (col.key?.includes("devis")) {
          params.query = { organisme: ligne.id };
        } else {
          params.query = { id: ligne.id };
        }
        this.$router.push(params);
      } else if (ligne._routeName) {
        if (ligne.collaborative && this.user.id !== ligne.utilisateur) {
          // Accès à la liste via collaboration
          this.$matomo.trackEvent(
            "collaboration_open",
            "click",
          );
        }
        let customLine = ligne;
        if (ligne.offre_defaut) {
          customLine = ligne.offre_defaut;
        } else if (ligne.offre) {
          customLine = ligne.offre;
        }

        let customOffer = null;
        if (ligne.offre_defaut || ligne.offre) {
          if (ligne?.offre?.ean) {
            customOffer = customLine?.offre;
          } else {
            customOffer = customLine?.manuel_numerique
            || customLine?.fourniture
            || customLine?.article;
          }
        }

        this.$router.push({
          name: ligne._routeName,
          params: { id: customOffer?.id || customLine.id },
        });
      } else {
        ligne._expanded = !ligne._expanded;
      }
    },
    /**
     * Tri des éléments selon la clé.
     * @param {Object} sortCol Colonne sur laquelle trier.
     */
    sort(sortCol) {
      // Si on trie plusieurs fois sur la même clé à la suite, on inverse l'ordre du tri.
      // Sinon on trie de façon croissante dès qu'on change de colonne.
      const key = sortCol.sortKey || sortCol.key;

      if (this.internalSort) {
        // Si le tri se fait en front, on emit les lignes triées.
        let sortedLines = this.rows;

        if (this.internalActiveSort.key === key) {
          // Si le dernier tri est sur la même colonne, on effectue l'inverse du précédent tri
          sortedLines = sortedLines.reverse();
          this.internalActiveSort.sortAscended = !this.internalActiveSort.sortAscended;
        } else {
          sortedLines = sortedLines.sort((a, b) => {
            if (a && b) {
              // On gère le cas d'un objet en profondeur
              const nestedKeys = key.split(".");
              const aKey = getNestedProperty(a, nestedKeys);
              const bKey = getNestedProperty(b, nestedKeys);

              if (Number.isNaN(Number.parseFloat(aKey)) || Number.isNaN(Number.parseFloat(bKey))) {
                if (aKey < bKey) return -1;
                if (aKey > bKey) return 1;
              } else {
                if (Number.parseFloat(aKey) < Number.parseFloat(bKey)) return -1;
                if (Number.parseFloat(aKey) > Number.parseFloat(bKey)) return 1;
              }
            }
            return 0;
          });
          this.internalActiveSort.sortAscended = true;
        }

        this.internalActiveSort.key = key;
        this.$emit("sort", sortedLines);
      } else {
        // Si le tri se fait via l'API, on emit la clé et le sens du tri pour refaire un fetch.
        const activeSort = { ...this.activeSort };
        if (activeSort.key === key) {
          activeSort.sortAscended = !activeSort.sortAscended;
        } else {
          activeSort.sortAscended = true;
        }

        activeSort.key = key;
        this.$emit("sort", activeSort);
      }
    },
    /**
     * Dirige vers la page indiquée par la ligne si le lien existe et on a activé la feature.
     * @param {Object} row Infos de la ligne.
     */
    handleRowClick(row) {
      if (row.product_link?.id && this.allowProductClick) {
        this.$router.push({
          name: row.product_link.name,
          params: { id: row.product_link.id },
        });
      }
    },
  },
};
</script>

<style lang="scss">
@use "@/assets/styles/components/table/table_classic.scss";
</style>
